import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../lib/api'
import { keys, useProjects } from '../lib/queries'
import { Button, Dialog, Field, Input, Select, Textarea, cx } from './ui'

// A short, distinct set: enough to tell boards apart at a glance in the rail,
// not so many that picking one is a decision.
const COLORS = ['#4457c9', '#0e7490', '#15803d', '#b45309', '#be123c', '#7c3aed', '#475569']

/** Derives SHOP from "Shop platform" so nobody has to invent a key. */
function suggestKey(name: string): string {
  const words = name.trim().toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean)
  if (words.length === 0) return ''
  const raw = words.length === 1 ? words[0]! : words.map((w) => w[0]!).join('')
  return raw.replace(/^[^A-Z]/, '').slice(0, 10)
}

export function NewProjectDialog({
  open, onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [description, setDescription] = useState('')
  const [color, setColor] = useState(COLORS[0]!)
  const [parentId, setParentId] = useState('')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const projects = useProjects()

  // Only a top-level project can be a parent: nesting is one level deep.
  const parentOptions = (projects.data ?? []).filter((p) => !p.parentId && !p.archivedAt)

  const create = useMutation({
    mutationFn: () =>
      api.createProject({ key, name, description, color, parentId: parentId || null }),
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: keys.projects() })
      reset()
      onOpenChange(false)
      navigate(`/p/${project.key}`)
    },
  })

  function reset() {
    setName('')
    setKey('')
    setKeyTouched(false)
    setDescription('')
    setColor(COLORS[0]!)
    setParentId('')
    create.reset()
  }

  function onNameChange(value: string) {
    setName(value)
    if (!keyTouched) setKey(suggestKey(value))
  }

  const error = create.error instanceof ApiError ? create.error : null

  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
      title="New project"
      description="A project is one board. Only the people you add can see it."
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" htmlFor="project-name" error={error?.fields?.name}>
          <Input
            id="project-name" autoFocus required value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Shop platform"
          />
        </Field>

        <Field
          label="Key" htmlFor="project-key" error={error?.fields?.key}
          hint="Used in task keys like SHOP-142. Uppercase letters and digits."
        >
          <Input
            id="project-key" required value={key}
            onChange={(e) => {
              setKeyTouched(true)
              setKey(e.target.value.toUpperCase())
            }}
            placeholder="SHOP"
            maxLength={10}
            className="task-key uppercase"
          />
        </Field>

        {parentOptions.length > 0 && (
          <Field
            label="Part of" htmlFor="project-parent" error={error?.fields?.parentId}
            hint="Make this a service inside a bigger project. The parent's board then shows everything underneath it."
          >
            <Select
              id="project-parent"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">Nothing — this is a top-level project</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Description" htmlFor="project-desc" error={error?.fields?.description}>
          <Textarea
            id="project-desc" rows={2} value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this board is for."
          />
        </Field>

        <Field label="Colour" error={error?.fields?.color}>
          <div className="flex gap-1.5">
            {COLORS.map((c) => (
              <button
                key={c} type="button" onClick={() => setColor(c)}
                aria-label={`Use colour ${c}`} aria-pressed={color === c}
                style={{ background: c }}
                className={cx(
                  'h-6 w-6 rounded-md transition-transform duration-[140ms]',
                  color === c
                    ? 'ring-2 ring-[var(--ink)] ring-offset-2 ring-offset-[var(--surface)]'
                    : 'hover:scale-110',
                )}
              />
            ))}
          </div>
        </Field>

        {error && Object.keys(error.fields ?? {}).length === 0 && (
          <p className="text-[13px] text-[var(--danger)]">{error.message}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending}>
            Create project
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
